// Automation release math.
//
// One automation reduces the time a scoped slice of the team spends on a set of
// OMCL sub-phases. Turning that into "FTE released" needs three joins:
//
//   1. Scope       — the automation's Discipline × Channel keys and levels pick
//                    which cells of the staffing basis it touches.
//   2. Concentration — `model.timeConcentration` says what share of a
//                    (sub-discipline, level) pair's time each sub-phase takes.
//                    A cell maps to exactly one sub-discipline, so its share is
//                    the sum over the automation's targeted sub-phases.
//   3. Ramp        — the scenario's deployment plan per automation decides how
//                    much of full impact has landed by a given year.
//
// Released hours for one (automation, cell) pair at full adoption are
// `cellFte × FTE_CAPACITY × share × reductionPct`. Everything else in here is
// aggregation over that one expression, which is the point: the Automation
// Impact tab, the Summary dollar tiles and the after-automation matrix all have to
// agree, and they only do so reliably if they share the expression rather than
// each re-deriving it.

import { SUB_DISCIPLINES } from '../maturity-taxonomy.js';
import { FTE_CAPACITY } from '../constants.js';
import { staffPlanCellKey } from './staff-plan.js';
import type {
  AutomationDeploymentSetting,
  AutomationImpactRow,
  DisciplineChannelCell,
  LevelId,
  Model,
  StaffPlanHorizon,
} from '../types.js';

/** Planning window a deployment ramp is expressed within. */
export const AUTOMATION_HORIZON_MONTHS = 36;
export const AUTOMATION_DEFAULT_RAMP_MONTHS = 12;
export const AUTOMATION_MIN_RAMP_MONTHS = 1;
export const AUTOMATION_MAX_RAMP_MONTHS = 24;

/** Below this an FTE figure is noise from float arithmetic, not headcount. */
const RELEASE_EPS = 1e-9;

/** Which point in the deployment to read impact at. `'full'` ignores the ramp
 *  entirely and answers "once every automation has fully landed". */
export type AutomationHorizon = 1 | 2 | 3 | 'full';

/** A resolved deployment plan — profile overrides layered on model defaults. */
export interface AutomationDeployment {
  startMonth: number;
  rampMonths: number;
  enabled: boolean;
}

function clampInt(
  v: number | undefined,
  min: number,
  max: number,
  fallback: number,
): number {
  if (typeof v !== 'number' || !isFinite(v)) return fallback;
  const i = Math.round(v);
  if (i < min) return min;
  if (i > max) return max;
  return i;
}

/** Layer a scenario's saved deployment setting over the automation's own
 *  default, clamping to the planning window. */
export function resolveAutomationDeployment(
  automation: AutomationImpactRow,
  setting?: AutomationDeploymentSetting,
): AutomationDeployment {
  return {
    startMonth: clampInt(setting?.startMonth, 0, AUTOMATION_HORIZON_MONTHS - 1, 0),
    rampMonths: clampInt(
      setting?.rampMonths,
      AUTOMATION_MIN_RAMP_MONTHS,
      AUTOMATION_MAX_RAMP_MONTHS,
      AUTOMATION_DEFAULT_RAMP_MONTHS,
    ),
    enabled:
      typeof setting?.enabled === 'boolean'
        ? setting.enabled
        : automation.active !== false,
  };
}

/** Resolve every automation in the model against the scenario's settings. */
export function resolveAutomationDeployments(
  automations: AutomationImpactRow[] | undefined,
  settings: Record<string, AutomationDeploymentSetting> | undefined,
): Map<string, AutomationDeployment> {
  const out = new Map<string, AutomationDeployment>();
  for (const a of automations || []) {
    if (!a) continue;
    out.set(a.id, resolveAutomationDeployment(a, settings?.[a.id]));
  }
  return out;
}

/** Persisted form of a horizon, for a saved plan's provenance. */
export function staffPlanHorizonFor(horizon: AutomationHorizon): StaffPlanHorizon {
  return horizon === 'full' ? 'full' : (`y${horizon}` as StaffPlanHorizon);
}

/** Short form, for tabs and chips. */
export function automationHorizonLabel(horizon: AutomationHorizon): string {
  return horizon === 'full' ? 'Full impact' : `Y${horizon}`;
}

/** Long form, for prose. */
export function automationHorizonWords(horizon: AutomationHorizon): string {
  return horizon === 'full' ? 'full impact' : `Year ${horizon}`;
}

/** Long form of a persisted horizon. Unknown or missing reads as full impact,
 *  matching how an older plan without the field was saved. */
export function staffPlanHorizonWords(
  horizon: StaffPlanHorizon | undefined,
): string {
  switch (horizon) {
    case 'y1':
      return 'Year 1';
    case 'y2':
      return 'Year 2';
    case 'y3':
      return 'Year 3';
    default:
      return 'full impact';
  }
}

function adoptionAt(month: number, startM: number, rampM: number): number {
  if (month < startM) return 0;
  if (rampM <= 0) return 1;
  if (month >= startM + rampM) return 1;
  return (month - startM) / rampM;
}

/**
 * Fraction of full impact realized at `horizon`.
 *
 * For a year, this is adoption averaged over that year's twelve months, sampled
 * mid-month — a saving that starts in month 6 only earns half of year 1. For
 * `'full'` it is 1 by definition.
 */
export function automationAdoption(
  horizon: AutomationHorizon,
  deployment: Pick<AutomationDeployment, 'startMonth' | 'rampMonths'>,
): number {
  if (horizon === 'full') return 1;
  const startMonth = (horizon - 1) * 12;
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += adoptionAt(
      startMonth + i + 0.5,
      deployment.startMonth,
      deployment.rampMonths,
    );
  }
  return sum / 12;
}

function channelLabelFor(model: Model, channelId: string): string | null {
  if (channelId === 'all') return null;
  const ch = (model.channels || []).find((c) => c.id === channelId);
  return ch?.label || channelId;
}

/**
 * The sub-discipline whose time-concentration profile a staffing cell follows.
 *
 * Most disciplines have exactly one sub-discipline; Investment and Analytics
 * split by channel, so those match on the channel label, tolerating the
 * `'A + B'` form used for shared legs. A cell with no channel (or an unmatched
 * one) falls back to the discipline's first leg rather than dropping out, since
 * dropping it would silently understate savings.
 */
export function mapCellToSubDiscKey(
  model: Model,
  cell: Pick<DisciplineChannelCell, 'disciplineId' | 'channelId'>,
): string | null {
  const candidates = SUB_DISCIPLINES.filter(
    (s) => s.parentDisciplineId === cell.disciplineId,
  );
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0]!.key;

  const cellLabel = channelLabelFor(model, cell.channelId);
  if (!cellLabel) return candidates[0]!.key;
  const labelLc = cellLabel.toLowerCase();

  const exact = candidates.find((s) => s.channel.toLowerCase() === labelLc);
  if (exact) return exact.key;

  const split = candidates.find((s) =>
    s.channel
      .split(/\s*\+\s*/)
      .some((part) => part.toLowerCase() === labelLc),
  );
  if (split) return split.key;

  const allLeg = candidates.find((s) => s.channel.toLowerCase() === 'all');
  if (allLeg) return allLeg.key;
  return candidates[0]!.key;
}

/** Presentation label for a basis cell's channel. */
export function basisChannelLabel(
  cell: Pick<DisciplineChannelCell, 'channelId' | 'channelLabel'>,
): string {
  if (cell.channelId === 'all') return 'All channels';
  return cell.channelLabel || cell.channelId;
}

/**
 * Total share of a (sub-discipline, level) pair's time that an automation's
 * sub-phases account for.
 *
 * An automation with no sub-phases selected releases nothing. Reading empty as
 * "every sub-phase" would let an unconfigured automation claim the whole
 * organisation's time, which is the more dangerous of the two mistakes.
 */
function sharesByKeyLevel(
  model: Model,
  subPhaseIds: string[] | undefined,
): Map<string, number> {
  const out = new Map<string, number>();
  const subPhases = new Set(subPhaseIds || []);
  if (subPhases.size === 0) return out;
  for (const row of model.timeConcentration || []) {
    if (!subPhases.has(row.subPhaseId)) continue;
    const k = `${row.subDisciplineKey}::${row.levelId}`;
    out.set(k, (out.get(k) || 0) + (Number(row.share) || 0));
  }
  return out;
}

/** One automation's effect on one staffing cell, at full adoption. */
export interface AutomationCellImpact {
  automationId: string;
  /** `discipline::channel::level`, matching `staffPlanCellKey`. */
  cellKey: string;
  disciplineId: string;
  disciplineLabel: string;
  channelId: string;
  channelLabel: string;
  levelId: LevelId;
  /** Headcount in the basis cell the release was derived from. */
  cellFte: number;
  /** Share of that cell's time the automation targets, 0..1. */
  share: number;
  hoursAtFull: number;
  fteAtFull: number;
}

function scopeMatches(
  automation: AutomationImpactRow,
  cell: DisciplineChannelCell,
): boolean {
  const dcKeys = automation.disciplineChannelKeys || [];
  if (
    dcKeys.length > 0 &&
    !dcKeys.includes(`${cell.disciplineId}::${cell.channelId}`)
  ) {
    return false;
  }
  const levels = automation.levelIds || [];
  if (levels.length > 0 && !levels.includes(cell.levelId)) return false;
  return true;
}

/**
 * Every (automation, cell) pair that releases time, at full adoption.
 *
 * Sorted by discipline, channel then level so callers can render without
 * re-sorting. Pairs releasing nothing are omitted.
 */
export function automationCellImpacts(
  model: Model,
  cells: DisciplineChannelCell[] | undefined,
  automations: AutomationImpactRow[] | undefined,
): AutomationCellImpact[] {
  const list = (automations || []).filter(Boolean);
  const basis = cells || [];
  if (list.length === 0 || basis.length === 0) return [];

  // Sub-discipline mapping is per cell, not per automation — resolve once.
  const subDiscByCell = new Map<DisciplineChannelCell, string | null>();
  for (const c of basis) subDiscByCell.set(c, mapCellToSubDiscKey(model, c));

  const out: AutomationCellImpact[] = [];
  for (const automation of list) {
    const reduction = Number(automation.reductionPct) || 0;
    if (reduction <= 0) continue;
    const shares = sharesByKeyLevel(model, automation.subPhaseIds);
    if (shares.size === 0) continue;

    for (const c of basis) {
      const cellFte = Number(c.adjustedFte) || 0;
      if (cellFte <= 0) continue;
      if (!scopeMatches(automation, c)) continue;
      const subDiscKey = subDiscByCell.get(c);
      if (!subDiscKey) continue;
      const share = shares.get(`${subDiscKey}::${c.levelId}`) || 0;
      if (share <= 0) continue;

      const hoursAtFull = cellFte * FTE_CAPACITY * share * reduction;
      if (hoursAtFull <= 0) continue;
      out.push({
        automationId: automation.id,
        cellKey: staffPlanCellKey(c.disciplineId, c.channelId, c.levelId),
        disciplineId: c.disciplineId,
        disciplineLabel: c.disciplineLabel,
        channelId: c.channelId,
        channelLabel: basisChannelLabel(c),
        levelId: c.levelId,
        cellFte,
        share,
        hoursAtFull,
        fteAtFull: hoursAtFull / FTE_CAPACITY,
      });
    }
  }

  return out.sort((a, b) => {
    if (a.disciplineLabel !== b.disciplineLabel) {
      return a.disciplineLabel.localeCompare(b.disciplineLabel);
    }
    if (a.channelLabel !== b.channelLabel) {
      return a.channelLabel.localeCompare(b.channelLabel);
    }
    return a.levelId.localeCompare(b.levelId);
  });
}

/** One automation's whole-basis effect at full adoption. */
export interface AutomationTotals {
  hoursAtFull: number;
  fteAtFull: number;
  /** Cells the scope covers, whether or not they release time — headcount can
   *  sit at a (sub-discipline, level) the concentration table is silent on. */
  matchedCellCount: number;
  matchedCellFte: number;
}

export function automationTotals(
  model: Model,
  cells: DisciplineChannelCell[] | undefined,
  automation: AutomationImpactRow,
): AutomationTotals {
  let matchedCellCount = 0;
  let matchedCellFte = 0;
  for (const c of cells || []) {
    const fte = Number(c.adjustedFte) || 0;
    if (fte <= 0) continue;
    if (!scopeMatches(automation, c)) continue;
    if (!mapCellToSubDiscKey(model, c)) continue;
    matchedCellCount += 1;
    matchedCellFte += fte;
  }

  let hoursAtFull = 0;
  for (const impact of automationCellImpacts(model, cells, [automation])) {
    hoursAtFull += impact.hoursAtFull;
  }

  return {
    hoursAtFull,
    fteAtFull: hoursAtFull / FTE_CAPACITY,
    matchedCellCount,
    matchedCellFte,
  };
}

/** Time released from one staffing cell by every enabled automation together. */
export interface AutomationCellRelease {
  cellKey: string;
  disciplineId: string;
  disciplineLabel: string;
  channelId: string;
  channelLabel: string;
  levelId: LevelId;
  /** Headcount before automation. */
  basisFte: number;
  /** Released FTE, capped at `basisFte`. */
  fteReleased: number;
  hoursReleased: number;
  /** True when stacked automations wanted more time than the cell holds, so
   *  `fteReleased` was capped. A cell cannot give back more than it has. */
  clamped: boolean;
  /** What each automation contributed, before the cap. */
  byAutomation: { automationId: string; fte: number; hours: number }[];
}

/**
 * Per-cell release across all automations, ramped to `horizon`.
 *
 * Disabled automations contribute nothing. The result is keyed by
 * `staffPlanCellKey` and only holds cells that release something, so callers
 * should treat a missing key as "untouched by automation".
 */
export function automationReleaseByCell(
  model: Model,
  cells: DisciplineChannelCell[] | undefined,
  settings: Record<string, AutomationDeploymentSetting> | undefined,
  horizon: AutomationHorizon,
): Map<string, AutomationCellRelease> {
  const automations = (model.automationImpacts || []).filter(Boolean);
  const deployments = resolveAutomationDeployments(automations, settings);
  const enabled = automations.filter((a) => deployments.get(a.id)?.enabled);

  const adoption = new Map<string, number>();
  for (const a of enabled) {
    adoption.set(
      a.id,
      automationAdoption(horizon, deployments.get(a.id)!),
    );
  }

  const out = new Map<string, AutomationCellRelease>();
  for (const impact of automationCellImpacts(model, cells, enabled)) {
    const ramp = adoption.get(impact.automationId) || 0;
    const fte = impact.fteAtFull * ramp;
    if (fte <= RELEASE_EPS) continue;

    let entry = out.get(impact.cellKey);
    if (!entry) {
      entry = {
        cellKey: impact.cellKey,
        disciplineId: impact.disciplineId,
        disciplineLabel: impact.disciplineLabel,
        channelId: impact.channelId,
        channelLabel: impact.channelLabel,
        levelId: impact.levelId,
        basisFte: impact.cellFte,
        fteReleased: 0,
        hoursReleased: 0,
        clamped: false,
        byAutomation: [],
      };
      out.set(impact.cellKey, entry);
    }
    entry.fteReleased += fte;
    entry.byAutomation.push({
      automationId: impact.automationId,
      fte,
      hours: impact.hoursAtFull * ramp,
    });
  }

  for (const entry of out.values()) {
    if (entry.fteReleased > entry.basisFte + RELEASE_EPS) {
      entry.clamped = true;
      entry.fteReleased = entry.basisFte;
    }
    entry.hoursReleased = entry.fteReleased * FTE_CAPACITY;
  }

  return out;
}

/** Post-automation headcount for one cell, floored at zero. */
export function automationResidualFte(
  basisFte: number,
  release: AutomationCellRelease | undefined,
): number {
  const left = basisFte - (release?.fteReleased || 0);
  return left > RELEASE_EPS ? left : 0;
}
